import React, { useCallback, useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import CommandPalette from './CommandPalette';
import { BreadcrumbProvider } from './Breadcrumb';
import { useGlobalShortcuts } from './useGlobalShortcuts';
import { useTheme } from './useTheme';
import { getPendingRequestsCount } from '../../servicesV2/partnerService';

const COLLAPSE_KEY = 'betterfly:sidebar:collapsed';

function readCollapsed() {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Shell — app chrome. Replaces the legacy MasterRoot.
 *
 *  - Sidebar (grouped, collapsible, persisted)
 *  - TopBar (breadcrumbs + ⌘K + bell + user menu)
 *  - CommandPalette (mounted once, portal-rendered)
 *  - Global keyboard shortcuts
 *  - One-time fetch of pending request count → sidebar + top-bar badges
 */
export function Shell() {
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [pendingRequests, setPendingRequests] = useState(0);
  const { theme, toggle: toggleTheme } = useTheme();

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
    } catch {}
  }, [collapsed]);

  useEffect(() => {
    getPendingRequestsCount()
      .then(setPendingRequests)
      .catch(() => setPendingRequests(0));
  }, []);

  const openPalette = useCallback(() => setPaletteOpen(true), []);
  const closePalette = useCallback(() => setPaletteOpen(false), []);

  useGlobalShortcuts({ onOpenPalette: openPalette });

  return (
    <BreadcrumbProvider>
      {/*
       * Layout: body-level scroll. Sidebar and TopBar are `sticky` so they
       * stay pinned while the document scrolls. <main> is a normal flow
       * element with no overflow — no nested scroll containers, no
       * scrollHeight miscalculations.
       */}
      <div className="flex min-h-screen bg-surface-muted text-fg">
        <Sidebar
          collapsed={collapsed}
          onToggle={() => setCollapsed((v) => !v)}
          badges={{ pendingRequests }}
        />
        <div className="flex min-w-0 flex-1 flex-col">
          <TopBar
            onOpenPalette={openPalette}
            pendingRequests={pendingRequests}
            theme={theme}
            onToggleTheme={toggleTheme}
          />
          <main className="flex-1 min-w-0 px-6 py-5">
            <Outlet />
          </main>
        </div>

        <CommandPalette open={paletteOpen} onClose={closePalette} />
      </div>
    </BreadcrumbProvider>
  );
}

export default Shell;
